import { useState, useEffect } from "react"
import { motion, AnimatePresence, useReducedMotion } from "framer-motion"

const DEVELOPERS = [
  { initials: "AK", name: "Alex K.", role: "Frontend", active: true },
  { initials: "JM", name: "Jordan M.", role: "Backend", active: true },
  { initials: "RS", name: "Riley S.", role: "Full-stack", active: true },
  { initials: "TL", name: "Taylor L.", role: "DevOps", active: true },
  { initials: "MC", name: "Morgan C.", role: "Backend", active: false },
  { initials: "DP", name: "Devon P.", role: "Frontend", active: false },
]

const MOBILE_DEVELOPERS = DEVELOPERS.slice(0, 4)

const ONBOARDING_STEPS = [
  "Day 1 Kickoff",
  "Day 3 First PR",
  "Week 2 Full speed",
  "Ongoing"
]

const COLUMNS = ["Backlog", "In Progress", "Review", "Merged"]
const MOBILE_COLUMNS = ["Backlog", "In Progress", "Merged"]

const NOTIFICATIONS = [
  { avatar: "AK", text: "@AK opened PR #284 · auth refactor" },
  { avatar: "✓", text: "#281 merged into main" },
  { avatar: "📅", text: "Standup in 10 min" },
]

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    const media = window.matchMedia(query)
    setMatches(media.matches)

    const listener = (e: MediaQueryListEvent) => setMatches(e.matches)
    media.addEventListener("change", listener)
    return () => media.removeEventListener("change", listener)
  }, [query])

  return matches
}

export function HeroTeamVelocity() {
  const prefersReducedMotion = useReducedMotion()
  const isMobile = useMediaQuery("(max-width: 640px)")

  const [timelineProgress, setTimelineProgress] = useState(prefersReducedMotion ? 100 : 0)
  const [cardPositions, setCardPositions] = useState([0, 1, 2, 3, 0, 1])
  const [commitBars, setCommitBars] = useState([12, 24, 18, 30, 14, 22, 28, 16, 20, 26, 18, 24, 32, 20])
  const [prCount, setPrCount] = useState(12)
  const [notificationIndex, setNotificationIndex] = useState(0)
  const [showNotification, setShowNotification] = useState(false)
  const [highlightedCard, setHighlightedCard] = useState<number | null>(null)

  const columns = isMobile ? MOBILE_COLUMNS : COLUMNS
  const developers = isMobile ? MOBILE_DEVELOPERS : DEVELOPERS

  useEffect(() => {
    if (prefersReducedMotion) return

    const interval = setInterval(() => {
      setTimelineProgress(prev => {
        if (prev >= 100) return 0
        return prev + (100 / 60)
      })
    }, 100)

    return () => clearInterval(interval)
  }, [prefersReducedMotion])

  useEffect(() => {
    if (prefersReducedMotion) return

    const maxCol = columns.length - 1

    const interval = setInterval(() => {
      setCardPositions(prev => {
        const newPositions = [...prev]

        const mergedCardIndex = newPositions.findIndex(pos => pos === maxCol)
        if (mergedCardIndex !== -1) {
          setHighlightedCard(mergedCardIndex)
          setTimeout(() => {
            setHighlightedCard(null)
            setCardPositions(current => {
              const updated = [...current]
              updated[mergedCardIndex] = 0
              return updated
            })
          }, 500)
        }

        return newPositions.map((pos, idx) => {
          if (idx === mergedCardIndex) return pos
          return Math.min(pos + 1, maxCol)
        })
      })
    }, 2000)

    return () => clearInterval(interval)
  }, [prefersReducedMotion, columns.length])

  useEffect(() => {
    if (prefersReducedMotion) return

    const interval = setInterval(() => {
      setCommitBars(prev => {
        const newBars = [...prev.slice(1), Math.floor(Math.random() * 24) + 8]
        return newBars
      })
    }, 3000)

    return () => clearInterval(interval)
  }, [prefersReducedMotion])

  useEffect(() => {
    if (prefersReducedMotion) return

    const interval = setInterval(() => {
      setPrCount(prev => prev + 1)
    }, 5000)

    return () => clearInterval(interval)
  }, [prefersReducedMotion])

  useEffect(() => {
    if (prefersReducedMotion) return

    const showInterval = setInterval(() => {
      setShowNotification(true)
      setTimeout(() => {
        setShowNotification(false)
        setNotificationIndex(prev => (prev + 1) % NOTIFICATIONS.length)
      }, 2000)
    }, 4000)

    return () => clearInterval(showInterval)
  }, [prefersReducedMotion])

  const activeStep = Math.floor((timelineProgress / 100) * ONBOARDING_STEPS.length)

  const getCardsInColumn = (colIndex: number) => {
    return cardPositions.map((pos, idx) => ({ pos, idx })).filter(c => c.pos === colIndex)
  }

  const fontSize = isMobile ? 1.15 : 1

  if (prefersReducedMotion) {
    return (
      <div className="w-full max-w-[720px] mx-auto" style={{ aspectRatio: "720/640" }}>
        <svg
          viewBox="0 0 720 640"
          width="100%"
          height="auto"
          role="img"
          aria-label="Animated dedicated developers team dashboard showing a senior engineering team, an onboarding timeline, a kanban board of in-flight tasks, and live velocity metrics."
        >
          <rect x="0" y="0" width="720" height="640" rx="16" fill="white" stroke="#E2E8F0" strokeWidth="2" />

          <g transform="translate(10, 10)">
            {developers.map((dev, i) => (
              <g key={i} transform={`translate(${i * 116}, 0)`}>
                <rect x="0" y="0" width="108" height="90" rx="8" fill="white" stroke="#E2E8F0" strokeWidth="1" />
                <circle cx="54" cy="30" r="16" fill="#FEE2E2" />
                <text x="54" y="35" textAnchor="middle" fontSize={12 * fontSize} fontWeight="600" fill="#ed2132">{dev.initials}</text>
                <rect x="24" y="54" width="60" height="8" rx="4" fill="#E2E8F0" />
                <rect x="34" y="68" width="40" height="6" rx="3" fill="#F1F5F9" />
                <circle cx="96" cy="78" r="4" fill={dev.active ? "#22C55E" : "#CBD5E1"} />
              </g>
            ))}
          </g>

          <g transform="translate(40, 120)">
            <line x1="0" y1="30" x2="640" y2="30" stroke="#E2E8F0" strokeWidth="2" />
            <line x1="0" y1="30" x2="640" y2="30" stroke="#ed2132" strokeWidth="2" />
            {ONBOARDING_STEPS.map((step, i) => (
              <g key={i} transform={`translate(${i * 160 + 80}, 0)`}>
                <circle cx="0" cy="30" r="8" fill="#ed2132" />
                <text x="0" y="60" textAnchor="middle" fontSize={11 * fontSize} fill="#475569">{step}</text>
              </g>
            ))}
          </g>

          <g transform="translate(20, 180)">
            {columns.map((col, i) => (
              <g key={i} transform={`translate(${i * (680 / columns.length)}, 0)`}>
                <rect x="0" y="0" width={660 / columns.length} height="24" rx="12" fill="#F1F5F9" />
                <text x={(660 / columns.length) / 2} y="16" textAnchor="middle" fontSize={11 * fontSize} fontWeight="600" fill="#64748B">{col}</text>
              </g>
            ))}
            {[0, 1, 2, 3, 4, 5].slice(0, isMobile ? 4 : 6).map((_, i) => {
              const colWidth = 660 / columns.length
              const colIndex = columns.length - 1
              return (
                <g key={i} transform={`translate(${colIndex * colWidth + 8}, ${40 + (i % 3) * 60})`}>
                  <rect x="0" y="0" width={colWidth - 16} height="52" rx="6" fill="white" stroke="#E2E8F0" strokeWidth="1" />
                  <circle cx="16" cy="26" r="8" fill="#FEE2E2" />
                  <text x="16" y="30" textAnchor="middle" fontSize={8 * fontSize} fontWeight="600" fill="#ed2132">{developers[i % developers.length].initials}</text>
                  <rect x="32" y="18" width={colWidth - 60} height="6" rx="3" fill="#E2E8F0" />
                  <rect x="32" y="30" width={colWidth - 80} height="6" rx="3" fill="#F1F5F9" />
                </g>
              )
            })}
          </g>

          <g transform={isMobile ? "translate(20, 420)" : "translate(20, 440)"}>
            <g transform="translate(0, 0)">
              {commitBars.map((h, i) => (
                <rect key={i} x={i * 16} y={60 - h} width="10" height={h} rx="2" fill={i === 13 ? "#ed2132" : "#CBD5E1"} />
              ))}
              <text x="0" y="80" fontSize={10 * fontSize} fill="#64748B">Commits / day</text>
            </g>

            <g transform={isMobile ? "translate(0, 100)" : "translate(280, 0)"}>
              <text x="0" y="30" fontSize={32 * fontSize} fontWeight="700" fill="#0F172A">12 PRs</text>
              <text x="0" y="50" fontSize={11 * fontSize} fill="#64748B">merged this week</text>
            </g>

            <g transform={isMobile ? "translate(0, 160)" : "translate(480, 0)"}>
              <polyline points="0,40 30,35 60,38 90,30 120,32 150,28 180,25" fill="none" stroke="#E2E8F0" strokeWidth="2" />
              <circle cx="180" cy="25" r="4" fill="#ed2132" />
              <text x="0" y="70" fontSize={10 * fontSize} fill="#64748B">Avg review: 1.4h</text>
            </g>
          </g>
        </svg>
      </div>
    )
  }

  return (
    <div className="w-full max-w-[720px] mx-auto" style={{ aspectRatio: "720/640" }}>
      <svg
        viewBox="0 0 720 640"
        width="100%"
        height="auto"
        role="img"
        aria-label="Animated dedicated developers team dashboard showing a senior engineering team, an onboarding timeline, a kanban board of in-flight tasks, and live velocity metrics."
      >
        <rect x="0" y="0" width="720" height="640" rx="16" fill="white" stroke="#E2E8F0" strokeWidth="2" />

        <g transform="translate(10, 10)">
          {developers.map((dev, i) => (
            <motion.g
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.3 }}
              transform={`translate(${i * (isMobile ? 172 : 116)}, 0)`}
            >
              <rect x="0" y="0" width={isMobile ? 164 : 108} height="90" rx="8" fill="white" stroke="#E2E8F0" strokeWidth="1" />
              <circle cx={isMobile ? 82 : 54} cy="30" r="16" fill="#FEE2E2" />
              <text x={isMobile ? 82 : 54} y="35" textAnchor="middle" fontSize={12 * fontSize} fontWeight="600" fill="#ed2132">{dev.initials}</text>
              <rect x={isMobile ? 52 : 24} y="54" width="60" height="8" rx="4" fill="#E2E8F0" />
              <rect x={isMobile ? 62 : 34} y="68" width="40" height="6" rx="3" fill="#F1F5F9" />
              {dev.active ? (
                <motion.circle
                  cx={isMobile ? 152 : 96}
                  cy="78"
                  r="4"
                  fill="#22C55E"
                  animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              ) : (
                <circle cx={isMobile ? 152 : 96} cy="78" r="4" fill="#CBD5E1" />
              )}
            </motion.g>
          ))}
        </g>

        <g transform="translate(40, 120)">
          <line x1="0" y1="30" x2="640" y2="30" stroke="#E2E8F0" strokeWidth="2" />
          <motion.line
            x1="0"
            y1="30"
            y2="30"
            stroke="#ed2132"
            strokeWidth="2"
            animate={{ x2: (timelineProgress / 100) * 640 }}
            transition={{ duration: 0.1 }}
          />
          {ONBOARDING_STEPS.map((step, i) => {
            const stepX = i * 160 + 80
            const isActive = (timelineProgress / 100) * 640 >= stepX
            return (
              <g key={i} transform={`translate(${stepX}, 0)`}>
                <motion.circle
                  cx="0"
                  cy="30"
                  r="8"
                  fill={isActive ? "#ed2132" : "white"}
                  stroke={isActive ? "#ed2132" : "#E2E8F0"}
                  strokeWidth="2"
                  animate={{ fill: isActive ? "#ed2132" : "white" }}
                />
                <text x="0" y="60" textAnchor="middle" fontSize={11 * fontSize} fill="#475569">{step}</text>
              </g>
            )
          })}
        </g>

        <g transform="translate(20, 180)">
          {columns.map((col, i) => {
            const colWidth = 680 / columns.length
            return (
              <g key={i} transform={`translate(${i * colWidth}, 0)`}>
                <rect x="0" y="0" width={colWidth - 8} height="24" rx="12" fill="#F1F5F9" />
                <text x={(colWidth - 8) / 2} y="16" textAnchor="middle" fontSize={11 * fontSize} fontWeight="600" fill="#64748B">{col}</text>
              </g>
            )
          })}

          {cardPositions.slice(0, isMobile ? 4 : 6).map((colIndex, cardIdx) => {
            const colWidth = 680 / columns.length
            const cardsInCol = getCardsInColumn(colIndex)
            const posInCol = cardsInCol.findIndex(c => c.idx === cardIdx)
            const isHighlighted = highlightedCard === cardIdx
            const isMerged = colIndex === columns.length - 1

            return (
              <motion.g
                key={cardIdx}
                animate={{
                  x: colIndex * colWidth + 4,
                  y: 36 + posInCol * 56
                }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              >
                <motion.rect
                  x="0"
                  y="0"
                  width={colWidth - 16}
                  height="50"
                  rx="6"
                  fill={isHighlighted ? "#FEE2E2" : "white"}
                  stroke="#E2E8F0"
                  strokeWidth="1"
                  animate={{ fill: isHighlighted ? "#FEE2E2" : "white" }}
                />
                <circle cx="16" cy="25" r="8" fill="#FEE2E2" />
                <text x="16" y="29" textAnchor="middle" fontSize={8 * fontSize} fontWeight="600" fill="#ed2132">
                  {developers[cardIdx % developers.length].initials}
                </text>
                <rect x="32" y="16" width={colWidth - 60} height="6" rx="3" fill="#E2E8F0" />
                <rect x="32" y="28" width={colWidth - 80} height="6" rx="3" fill="#F1F5F9" />
                {isMerged && isHighlighted && (
                  <motion.g
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <circle cx={colWidth - 28} cy="25" r="10" fill="#22C55E" />
                    <path d={`M ${colWidth - 33} 25 L ${colWidth - 29} 29 L ${colWidth - 23} 21`} stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </motion.g>
                )}
              </motion.g>
            )
          })}
        </g>

        <g transform={isMobile ? "translate(20, 420)" : "translate(20, 440)"}>
          <g transform="translate(0, 0)">
            {commitBars.map((h, i) => (
              <motion.rect
                key={i}
                x={i * 16}
                width="10"
                rx="2"
                fill={i === 13 ? "#ed2132" : "#CBD5E1"}
                initial={{ height: 0, y: 60 }}
                animate={{ height: h, y: 60 - h }}
                transition={{ duration: 0.3 }}
              />
            ))}
            <motion.rect
              x={13 * 16}
              y={60 - commitBars[13]}
              width="10"
              height={commitBars[13]}
              rx="2"
              fill="#ed2132"
              animate={{ opacity: [1, 0.6, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
            <text x="0" y="80" fontSize={10 * fontSize} fill="#64748B">Commits / day</text>
          </g>

          <g transform={isMobile ? "translate(0, 100)" : "translate(280, 0)"}>
            <motion.text
              key={prCount}
              x="0"
              y="30"
              fontSize={32 * fontSize}
              fontWeight="700"
              fill="#0F172A"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {prCount} PRs
            </motion.text>
            <text x="0" y="50" fontSize={11 * fontSize} fill="#64748B">merged this week</text>
          </g>

          <g transform={isMobile ? "translate(0, 160)" : "translate(480, 0)"}>
            <polyline
              points="0,40 30,35 60,38 90,30 120,32 150,28 180,25"
              fill="none"
              stroke="#E2E8F0"
              strokeWidth="2"
            />
            <motion.circle
              cx="180"
              cy="25"
              r="4"
              fill="#ed2132"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
            <text x="0" y="70" fontSize={10 * fontSize} fill="#64748B">Avg review: 1.4h</text>
          </g>
        </g>

        <AnimatePresence>
          {showNotification && (
            <motion.g
              initial={{ x: isMobile ? 0 : 250, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: isMobile ? 0 : 250, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <g transform={isMobile ? "translate(20, 580)" : "translate(460, 16)"}>
                <rect x="0" y="0" width={isMobile ? 680 : 240} height={isMobile ? 44 : 56} rx="8" fill="white" stroke="#E2E8F0" strokeWidth="1" />
                <circle cx="24" cy={isMobile ? 22 : 28} r="12" fill="#FEE2E2" />
                <text x="24" y={isMobile ? 26 : 32} textAnchor="middle" fontSize={10 * fontSize} fontWeight="600" fill="#ed2132">
                  {NOTIFICATIONS[notificationIndex].avatar}
                </text>
                <text x="44" y={isMobile ? 26 : 32} fontSize={11 * fontSize} fill="#475569">
                  {NOTIFICATIONS[notificationIndex].text}
                </text>
              </g>
            </motion.g>
          )}
        </AnimatePresence>
      </svg>
    </div>
  )
}
